/**
 * Admin → Legal y Compliance
 * Hub de gestión de documentos legales, aceptaciones y KYC.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  FileText, Users, ShieldCheck, ClipboardList, Eye,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { reconcileLegalSeedCoverage } from "@/lib/legal-seed-coverage";
import {
  LEGAL_TYPE_LABELS,
  VersionStatusBadge,
  pickLatestVersion,
  pickPublishedVersion,
} from "@/components/legal/legal-admin-shared";

export const Route = createFileRoute("/admin/ops/legal/")({
  component: LegalIndexPage,
  head: () => ({ meta: [{ title: "Legal y Compliance — Admin" }] }),
});

interface VersionSummary {
  id: string;
  version: string;
  status: string;
  createdAt: string;
  publishedAt?: string | null;
  requiresReacceptance?: boolean;
}

interface DocSummary {
  id: string;
  type: string;
  title: string;
  status: string;
  updatedAt: string;
  publishedVersion: { version: string; publishedAt: string } | null;
  versions: VersionSummary[];
}

function LegalIndexPage() {
  const [docs, setDocs] = useState<DocSummary[]>([]);
  const [expectedTypes, setExpectedTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/admin/legal")
      .then((r) => r.json())
      .then((d: { documents?: DocSummary[]; expectedTypes?: string[] }) => {
        setDocs(d.documents ?? []);
        setExpectedTypes(d.expectedTypes ?? []);
        setLoading(false);
      })
      .catch(() => {
        setError("Error cargando documentos");
        setLoading(false);
      });
  }, []);

  const coverage = expectedTypes.length
    ? reconcileLegalSeedCoverage(docs.map((d) => d.type), expectedTypes)
    : null;

  const pendingReviewCount = docs.reduce(
    (n, d) => n + d.versions.filter((v) => v.status === "in_review" || v.status === "approved").length,
    0,
  );

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Legal y Compliance</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Documentos legales, aceptaciones de Tenants y verificación KYC
          </p>
        </div>
      </div>

      <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-5">
        {[
          { icon: FileText, label: "Documentos", href: "/admin/ops/legal/documents", desc: "Gestionar versiones" },
          {
            icon: Eye,
            label: "Versiones pendientes",
            href: "/admin/ops/legal/documents",
            desc: pendingReviewCount > 0 ? `${pendingReviewCount} en revisión/aprobación` : "Sin pendientes",
            badge: pendingReviewCount > 0 ? pendingReviewCount : undefined,
          },
          { icon: Users, label: "Aceptaciones", href: "/admin/ops/legal/acceptances", desc: "Historial por tenant" },
          { icon: ShieldCheck, label: "KYC", href: "/admin/ops/legal/kyc", desc: "Estados de verificación" },
          { icon: ClipboardList, label: "Auditoría", href: "/admin/ops/audit-log", desc: "Eventos legales" },
        ].map((hub) => (
          <Link key={hub.label} to={hub.href as never}>
            <Card className="cursor-pointer transition-colors hover:bg-muted/50">
              <CardContent className="p-4">
                <div className="mb-2 flex items-center gap-2">
                  <hub.icon className="h-5 w-5 text-muted-foreground" />
                  {hub.badge !== undefined && (
                    <span className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                      {hub.badge}
                    </span>
                  )}
                </div>
                <p className="font-semibold text-foreground">{hub.label}</p>
                <p className="text-xs text-muted-foreground">{hub.desc}</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Documentos legales</CardTitle>
          <Link to="/admin/ops/legal/documents" search={{} as never}>
            <Button size="sm" variant="outline">Gestionar</Button>
          </Link>
        </CardHeader>
        <CardContent className="p-0" data-testid="legal-hub-documents">
          {loading && (
            <p className="px-6 py-4 text-sm text-muted-foreground">Cargando…</p>
          )}
          {error && (
            <p className="px-6 py-4 text-sm text-destructive">{error}</p>
          )}
          {!loading && !error && docs.length === 0 && (
            <div className="px-6 py-8 text-center">
              <p className="text-sm text-muted-foreground">
                No hay documentos legales en la DB.
              </p>
            </div>
          )}
          {!loading && docs.map((doc) => {
            const latest = pickLatestVersion(doc.versions);
            const published = doc.publishedVersion ?? pickPublishedVersion(doc.versions);
            const needsReacc = doc.versions.some(
              (v) => v.status === "published" && v.requiresReacceptance,
            );
            return (
              <div
                key={doc.id}
                className="flex items-center justify-between border-t border-border px-6 py-3"
                data-testid="legal-doc-row"
                data-doc-type={doc.type}
                data-latest-status={latest?.status ?? ""}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-foreground">
                    {LEGAL_TYPE_LABELS[doc.type] ?? doc.title}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {doc.type}
                    {published
                      ? ` · publicada ${published.version}`
                      : " · sin versión publicada"}
                    {latest ? ` · última ${latest.version}` : ""}
                    {doc.updatedAt
                      ? ` · actualizado ${new Date(doc.updatedAt).toLocaleDateString("es-AR")}`
                      : ""}
                  </p>
                </div>
                <div className="ml-4 flex items-center gap-3">
                  {latest && <VersionStatusBadge status={latest.status} />}
                  {needsReacc && (
                    <span className="text-[10px] font-medium uppercase tracking-wide text-amber-700">
                      Reacceptance
                    </span>
                  )}
                  <Link
                    to="/admin/ops/legal/documents/$docId"
                    params={{ docId: doc.id }}
                    search={{} as never}
                  >
                    <Button size="sm" variant="outline">Ver documento</Button>
                  </Link>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {coverage && !coverage.reconciled && (
        <div
          className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950"
          data-testid="legal-seed-alert"
        >
          <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
            Documentos pendientes de seed
          </p>
          <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
            {coverage.presentCount} / {coverage.expectedCount} documentos cargados.
            Faltan: {coverage.missing.join(", ")}.
            Los documentos legales existen en markdown en{" "}
            <code>_docs/legal/</code> (fallback <code>_docs/docs-system/legal/</code>)
            pero no están todos en la DB. Ejecutar{" "}
            <code>node scripts/seed-legal-documents.mjs</code>.
          </p>
        </div>
      )}
    </div>
  );
}
